import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import BrandTooltip from 'components/consumer/BrandTooltip';
import PDPNotification from 'components/consumer/PDP/PDPNotification';
import IconQuickship from 'components/icons/iconQuickship';
import ProgressLoaderDots from 'components/essentials/ProgressLoaderDots/ProgressLoaderDots';
import {
  PDP_READYTOSHIP_TAB_ID,
  PDP_DECIDELATER_TAB_ID,
} from 'commons/constants';
import usePDPLeadTime from 'data-hooks/usePDPLeadTime';
import useScreen from 'hooks/useScreen';
import BrowserOnly from 'components/consumer/BrowserOnly';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import useIsZipCodeFromUserServiceable from 'global-state/isZipCodeFromUserServiceable/useIsZipCodeFromUserServiceable';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import PDPZipCodeEditLocation from '../PDPZipCodeEditLocation';

const PDPLeadTimeUI = ({ mode }) => {
  const { shippingLeadTime, leadTimeLoading } = usePDPLeadTime();

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const userLocation = useUserLocation();
  const isUserInUS = userLocation?.country === 'United States';

  const zipCodeFromUser = useZipCodeFromUser();

  const isRTS = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID;
  const isDL = pdpSelectedCustomizerTab === PDP_DECIDELATER_TAB_ID;

  const [showMobileTooltip, setShowMobileTooltip] = useState(false);
  const { isMobileOrTablet } = useScreen();

  const onTooltipToggle = useCallback(isTooltipOpen => {
    setShowMobileTooltip(isTooltipOpen);
  }, []);

  const toolTipMessage = `Please note, this is the estimated time it will take to build and prepare your order for shipment. This is not a delivery date. Full delivery estimates are shown once you're done adding products to your order at checkout.`;

  const closeTooltip = useCallback(() => {
    setShowMobileTooltip(false);
  }, []);

  const isZipCodeFromUserServiceable = useIsZipCodeFromUserServiceable();
  const isUnavailableToShip =
    !!zipCodeFromUser && !isZipCodeFromUserServiceable;

  return (
    <>
      <div className="flex flex-row items-center min-h-[15px] mt-4 justify-center text-gray text-base text-ellipsis whitespace-nowrap flex-wrap max-[767px]:[justify-content:left] max-[767px]:mr-0">
        {isUnavailableToShip && (
          <span>Unavailable to ship to {zipCodeFromUser}.</span>
        )}

        {!isUnavailableToShip && (!!leadTimeLoading || !!shippingLeadTime) && (
          <>
            {!!isRTS && (
              <span className="flex items-center mr-[2px]">
                <IconQuickship />{' '}
              </span>
            )}

            <span className="mr-[3px] text-gray">
              {isRTS
                ? 'Ready to ship '
                : isDL
                ? 'Ship time'
                : 'Available to ship '}
            </span>
          </>
        )}

        {!isUnavailableToShip && !!leadTimeLoading && (
          <BrowserOnly>
            <ProgressLoaderDots inline grey className="m-0 mx-1" />
          </BrowserOnly>
        )}

        {!isUnavailableToShip && !leadTimeLoading && !!shippingLeadTime && (
          <span>{isDL ? 'TBD' : `in ${shippingLeadTime}`}</span>
        )}

        <span>&nbsp;</span>

        {(isUnavailableToShip ||
          (!isUnavailableToShip && (!!leadTimeLoading || !!shippingLeadTime)) ||
          !isUserInUS) && <PDPZipCodeEditLocation />}

        {!isUnavailableToShip && (
          <div>
            <BrandTooltip
              className="bottom-0.5"
              direction={mode === 'quickview' ? 'top' : 'bottom'}
              hideIconOnOpenMobile
              iconClassName="!top-[3px]"
              iconSize="big"
              inactiveOnMobile
              mode={mode}
              onTooltipToggle={onTooltipToggle}
              openOnHover
              showCloseButton={false}
            >
              {toolTipMessage}
            </BrandTooltip>
          </div>
        )}
      </div>

      {showMobileTooltip && isMobileOrTablet && (
        <PDPNotification
          heading="Ready to Ship"
          subheading={toolTipMessage}
          onClose={closeTooltip}
        />
      )}
    </>
  );
};

PDPLeadTimeUI.propTypes = {
  mode: PropTypes.oneOf(['quickview', 'pdp']),
};

export default PDPLeadTimeUI;
